import { useState } from 'react';

const PROJECTS = [
  {
    id: 1,
    title: 'Gitsawe Web App',
    category: 'web',
    description:
      'A comprehensive digital platform for Ethiopian Orthodox Tewahedo Church services. Features daily Gitsawe & Sinksar readings, prayer books (Mahlet, Wudasie Mariam, Seatat), Ethiopian Orthodox calendar, Bahire Hasab calculator, news system, and an integrated Telegram bot.',
    technologies: ['React', 'Node.js', 'MongoDB', 'Tailwind CSS', 'Firebase', 'Cloudinary', 'Vercel'],
    liveUrl: 'https://gitsawe-tailwind-2019.vercel.app',
    githubUrl: 'https://github.com/hailemariam-eyayu/gitsawe-tailwind-2019',
    icon: '⛪',
    badge: 'Live',
    badgeColor: 'badge-live',
    gradient: 'card-gradient-amber',
  },
  {
    id: 2,
    title: 'Gitsawe Flutter App',
    category: 'mobile',
    description:
      'A cross-platform mobile application for Ethiopian Orthodox Church content. Includes offline support, daily readings, prayer books, Ethiopian calendar, Bahire Hasab calculator, dark/light mode, and push notifications.',
    technologies: ['Flutter', 'Dart', 'SQLite', 'Firebase', 'Cloudinary'],
    liveUrl: 'https://github.com/hailemariam-eyayu/gitsaweflutterapk/releases',
    githubUrl: 'https://github.com/hailemariam-eyayu/gitsaweflutterapk',
    icon: '📱',
    badge: 'APK',
    badgeColor: 'badge-blue',
    gradient: 'card-gradient-blue',
  },
  {
    id: 3,
    title: 'Gitsawe Telegram Bot',
    category: 'bot',
    description:
      'An automated Telegram bot that sends daily Gitsawe and Sinksar readings to subscribers. Supports channel broadcasting, subscriber management, and scheduled daily messages via cron jobs.',
    technologies: ['Node.js', 'Telegram Bot API', 'MongoDB', 'Vercel', 'node-cron'],
    liveUrl: 'https://t.me/GitsaweBot',
    githubUrl: 'https://github.com/hailemariam-eyayu/gitsawetelegrambot',
    icon: '🤖',
    badge: 'Live',
    badgeColor: 'badge-live',
    gradient: 'card-gradient-indigo',
  },
  {
    id: 4,
    title: 'Developer Portfolio',
    category: 'web',
    description:
      'A modern dark-themed developer portfolio built with React + Vite + Tailwind CSS. Features animated stats, filterable project cards, scroll-reveal animations, certificate showcase with live links, and a contact form backed by Express + PostgreSQL.',
    technologies: ['React', 'Vite', 'Tailwind CSS', 'TypeScript', 'Express.js', 'PostgreSQL'],
    liveUrl: 'https://hailemariam-eyayu.vercel.app',
    githubUrl: 'https://github.com/hailemariam-eyayu/developer_portfolio',
    icon: '💼',
    badge: 'Live',
    badgeColor: 'badge-live',
    gradient: 'card-gradient-purple',
  },
  {
    id: 5,
    title: 'Auto Workshop Management',
    category: 'mobile',
    description:
      'A Flutter-based auto workshop management system with Google Drive backup integration. Manages customer records, vehicle service history, invoices, employee records, and automated cloud backups.',
    technologies: ['Flutter', 'Dart', 'SQLite', 'Google Drive API'],
    liveUrl: null,
    githubUrl: null,
    icon: '🔧',
    badge: 'Private',
    badgeColor: 'badge-gray',
    gradient: 'card-gradient-orange',
  },
  {
    id: 6,
    title: 'Dormitory Management System',
    category: 'web',
    description:
      'An academic project built during university — a full-stack dormitory management system for Debre Markos University. Handles student room allocation, maintenance requests, and administrative reporting.',
    technologies: ['Laravel', 'PHP', 'MySQL', 'Bootstrap'],
    liveUrl: null,
    githubUrl: null,
    icon: '🏫',
    badge: 'Academic',
    badgeColor: 'badge-teal',
    gradient: 'card-gradient-teal',
  },
];

const CATEGORIES = [
  { key: 'all', label: 'All Projects', icon: '🗂️' },
  { key: 'web', label: 'Web', icon: '🌐' },
  { key: 'mobile', label: 'Mobile', icon: '📱' },
  { key: 'bot', label: 'Bot', icon: '🤖' },
];

const BADGE_CLASSES = {
  'badge-live':  'bg-green-100 text-green-700 border border-green-200',
  'badge-blue':  'bg-blue-100 text-blue-700 border border-blue-200',
  'badge-gray':  'bg-gray-100 text-gray-600 border border-gray-200',
  'badge-teal':  'bg-teal-100 text-teal-700 border border-teal-200',
};

const GRADIENT_CLASSES = {
  'card-gradient-amber':  'from-amber-50 to-orange-50 border-amber-200',
  'card-gradient-blue':   'from-blue-50 to-cyan-50 border-blue-200',
  'card-gradient-indigo': 'from-indigo-50 to-violet-50 border-indigo-200',
  'card-gradient-purple': 'from-purple-50 to-pink-50 border-purple-200',
  'card-gradient-orange': 'from-orange-50 to-red-50 border-orange-200',
  'card-gradient-teal':   'from-teal-50 to-emerald-50 border-teal-200',
};

const ProjectCard = ({ project }) => {
  const gradientClass = GRADIENT_CLASSES[project.gradient] || '';
  const badgeClass = BADGE_CLASSES[project.badgeColor] || '';

  return (
    <div
      className={`bg-gradient-to-br ${gradientClass} border rounded-xl p-5 flex flex-col gap-3 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5`}
      style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{project.icon}</span>
          <h3 className="font-bold text-base" style={{ color: 'var(--color-text)' }}>
            {project.title}
          </h3>
        </div>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${badgeClass}`}>
          {project.badge}
        </span>
      </div>

      {/* Description */}
      <p className="text-sm leading-relaxed" style={{ color: 'var(--color-muted)' }}>
        {project.description}
      </p>

      {/* Tech stack */}
      <div className="flex flex-wrap gap-1.5">
        {project.technologies.map((tech) => (
          <span
            key={tech}
            className="text-xs px-2 py-0.5 rounded-full font-medium"
            style={{
              background: 'rgba(37,99,235,0.08)',
              color: 'var(--color-primary)',
              border: '1px solid rgba(37,99,235,0.15)',
            }}
          >
            {tech}
          </span>
        ))}
      </div>

      {/* Links */}
      <div className="flex gap-2 mt-auto pt-1">
        {project.liveUrl && (
          <a
            href={project.liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-opacity hover:opacity-85"
            style={{ background: 'linear-gradient(135deg, var(--color-primary), var(--color-accent))' }}
          >
            🔗 Live Demo
          </a>
        )}
        {project.githubUrl && (
          <a
            href={project.githubUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
            style={{
              background: 'var(--color-bg)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text)',
            }}
          >
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
            </svg>
            GitHub
          </a>
        )}
        {!project.liveUrl && !project.githubUrl && (
          <span className="text-xs px-3 py-1.5 rounded-lg" style={{ color: 'var(--color-muted)', background: 'var(--color-bg)', border: '1px solid var(--color-border)' }}>
            🔒 Private / Internal
          </span>
        )}
      </div>
    </div>
  );
};

const Projects = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const filtered = activeCategory === 'all'
    ? PROJECTS
    : PROJECTS.filter((p) => p.category === activeCategory);

  return (
    <div className="max-w-5xl mx-auto">
      {/* Page Header */}
      <div className="page-header mb-4">
        <h1 className="page-main-title">My Projects</h1>
        <div className="page-description">
          <p className="page-description-main">Software projects I've built — web apps, mobile apps, and bots</p>
          <p className="page-description-sub">{PROJECTS.length} projects · Click links to explore</p>
        </div>
      </div>

      {/* Category Filter */}
      <div
        className="flex flex-wrap gap-2 mb-5 p-3 rounded-xl"
        style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}
      >
        {CATEGORIES.map((cat) => {
          const count = cat.key === 'all' ? PROJECTS.length : PROJECTS.filter((p) => p.category === cat.key).length;
          const isActive = activeCategory === cat.key;
          return (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive ? 'text-white shadow-sm' : 'hover:opacity-80'
              }`}
              style={
                isActive
                  ? { background: 'linear-gradient(135deg, var(--color-primary), var(--color-accent))' }
                  : { background: 'var(--color-bg)', border: '1px solid var(--color-border)', color: 'var(--color-muted)' }
              }
            >
              <span>{cat.icon}</span>
              <span>{cat.label}</span>
              <span
                className="text-xs px-1.5 py-0.5 rounded-full font-bold"
                style={
                  isActive
                    ? { background: 'rgba(255,255,255,0.25)', color: '#fff' }
                    : { background: 'rgba(37,99,235,0.1)', color: 'var(--color-primary)' }
                }
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map((project) => (
          <ProjectCard key={project.id} project={project} />
        ))}
      </div>

      {/* Footer note */}
      <div className="mt-6 text-center">
        <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
          Built by Hailemariam Eyayu · Software Engineer · Addis Ababa, Ethiopia
        </p>
      </div>
    </div>
  );
};

export default Projects;
